import { getLastPlayerState } from "@api/player";
import { getUser } from "@api/user";
import type { User } from "@app/api/user/types";
import LoadingOverlay from "@components/LoadingOverlay";
import useAuth from "@hooks/useAuth";
import useSignOut from "@hooks/useSignOut";
import * as Sentry from "@sentry/browser";
import { useLocation, useNavigate } from "@solidjs/router";
import { ENV } from "@src/env";
import mixpanel from "mixpanel-browser";
import { type ParentComponent, Show, batch, createContext, createEffect, createSignal, useContext } from "solid-js";
import { type SetStoreFunction, createStore } from "solid-js/store";
import { ActiveFeedProvider } from "../ActiveFeed";
import { GoogleProvider } from "../Google";
import { GroupRolesProvider } from "../GroupRoles";
import { NotificationsProvider } from "../Notifications";
import { playerContext } from "../Player";
import type { PlayerTrack } from "../Player/types";

export const emptyUser: User = {
	activity: 0,
	background: "/logo_no_text.png",
	followed: false,
	followers: 0,
	nickname: "",
	photo: "/logo_no_text.png",
	private: false,
	uid: "",
	blocked: false,
	following: 0,
	tracklists: 0,
};

export const userContext = createContext<{
	user: User;
	setUser: SetStoreFunction<User>;
}>({
	user: emptyUser,
	setUser: () => {},
});

export const UserProvider: ParentComponent = (props) => {
	const location = useLocation();
	const [user, setUser] = createStore<User>(structuredClone(emptyUser));
	const [loaded, setLoaded] = createSignal(false);
	const { currentUser, loading } = useAuth();
	const signOut = useSignOut();
	const navigate = useNavigate();
	const { setPlayer } = useContext(playerContext);

	createEffect(() => {
		const c = currentUser();
		const l = loading();
		if (c && !l) {
			getUser(c.uid)
				.then((res) => {
					setUser(res);
					setLoaded(true);
					if (ENV.VITE_MIXPANEL) {
						mixpanel.identify(res.uid);
						mixpanel.register({
							username: res.nickname,
							user_id: res.uid,
							email: c.email,
						});
					}
					Sentry.setUser({
						email: c.email ?? undefined,
						id: res.uid,
						username: res.nickname,
					});
					getLastPlayerState().then((resState) => {
						if (!resState) return;
						batch(() => {
							setPlayer("resume", false);
							setPlayer("track", 0);
							setPlayer(
								"queue",
								resState.tracks.map<PlayerTrack>((t, i) => ({
									...t,
									tracklistUuid: resState.uuid,
									index: i,
									author: t.author.nickname,
									track: t,
									type: "TRACK",
								})),
							);
							setPlayer("feed", { type: "TRACKLIST" });
						});
					});
				})
				.catch(signOut);
			return;
		}
		if (!c && !l) {
			if (location.pathname.includes("/t/") || location.pathname.includes("/tracklist/"))
				return navigate(`/public${location.pathname}`);
			signOut();
		}
	});

	return (
		<GoogleProvider>
			<userContext.Provider value={{ user, setUser }}>
				<Show when={loaded()} fallback={<LoadingOverlay />}>
					<GroupRolesProvider>
						<ActiveFeedProvider>
							<NotificationsProvider>{props.children}</NotificationsProvider>
						</ActiveFeedProvider>
					</GroupRolesProvider>
				</Show>
			</userContext.Provider>
		</GoogleProvider>
	);
};
